import { useEffect } from 'react'
import { intl } from '../../i18n'

// 進捗報告画面の初期表示で自動的に開く「フォルダ一覧」（レポート選択メニュー）を、
// ×ボタンだけでなくメニュー外のクリックでも閉じられるようにする。
//
// 本体（view/pages/ProjectVersionReport）はこのメニューを Slide で描画しており、
// Modal/ClickAwayListener を使っていないため外側クリックで閉じる経路が無い。
// デモでは初期表示時に必ず開くので（index.tsx の storedVersionUuid 未設定時に
// openVersionSelectMenu）、来訪者が画面を触ろうとして閉じ方が分からず止まる。
//
// DemoVersionReportDisabledFeatures と同じく、本体は改修せずこの画面が
// マウントされている間だけ document の capture フェーズでクリックを監視する方式。
// デモ固有の都合を本体へ持ち込まないため。
//
// 閉じる操作は本体の×ボタンを click() することで行う。onClose を直接呼ぶ口が
// 無く、×ボタンのクリックは本体が持つ閉じる処理そのものなので、
// 挙動が確実に一致する。

// メニュー内のパンくずは TemplateMenu（フォルダ一覧）でも
// ReportMenu（フォルダ配下のレポート一覧）でもこの文言を含むため、
// どちらの状態でもメニュー本体の起点として使える。本体がハードコードして
// いる文言なのでロケールに依存しない（i18n 経由にすると英語ロケールで外れる）。
const MENU_BREADCRUMB_TEXT = 'フォルダ一覧'

// メニュー本体の×ボタン。commons.tsx の CloseButton は CloseRoundedIcon を
// 使っており、MUI のアイコンはビルド構成に関わらずこの data-testid を出力する。
// :has() は対応していないブラウザで querySelector が例外を投げるため、
// アイコンを引いてから closest でボタンへ辿る。
const CLOSE_ICON_SELECTOR = '[data-testid="CloseRoundedIcon"]'

// メニューから開かれる Dialog / Popper（フォルダ作成、リネームメニュー等）は
// portal で body 直下に描画されメニューの外側になる。これらの操作で
// メニューが閉じないよう除外する。
const PORTAL_SELECTOR = '.MuiModal-root,.MuiPopper-root'

// ヘッダーのメニュー開閉ボタン。ここを除外しないと、こちらが閉じた直後に
// 本体の toggle が走って開き直してしまう。Tooltip の title がそのまま
// aria-label になる（describeChild 未指定時の MUI の既定動作）。
const TOGGLE_BUTTON_SELECTOR = `[aria-label="${intl.formatMessage({
  id: 'versionReport.tooltip.selectReport',
})}"]`

// パンくずから見て最初に position: absolute になる祖先がメニュー本体
// （Slide でラップされた DialogContainer）。クラス名は emotion の自動生成で
// 安定しないため、構造ではなく配置の性質で特定する。
//
// Slide は閉じている間も要素を残し visibility: hidden にするだけなので、
// 見えているかどうかも併せて判定する（閉じた状態のメニューに対して
// ×ボタンを押してしまわないように）。
const findOpenMenu = (): HTMLElement | null => {
  const breadcrumb = Array.from(
    document.querySelectorAll<HTMLElement>('span')
  ).find(element => element.textContent?.trim() === MENU_BREADCRUMB_TEXT)
  if (!breadcrumb) return null
  let current: HTMLElement | null = breadcrumb.parentElement
  while (current) {
    const style = window.getComputedStyle(current)
    if (style.position === 'absolute') {
      return style.visibility === 'hidden' ? null : current
    }
    current = current.parentElement
  }
  return null
}

export const DemoVersionSelectMenuOutsideClose = () => {
  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      const menu = findOpenMenu()
      if (!menu || menu.contains(target)) return
      if (target.closest(PORTAL_SELECTOR)) return
      if (target.closest(TOGGLE_BUTTON_SELECTOR)) return
      const closeButton = menu
        .querySelector(CLOSE_ICON_SELECTOR)
        ?.closest('button')
      closeButton?.click()
    }

    document.addEventListener('mousedown', closeOnOutsideClick, true)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick, true)
    }
  }, [])

  return null
}
